import React, { Component, PropTypes } from 'react';
import { buildURLFingerprinting } from '../../shared/utils/UrlUtils';
if (typeof window !== 'undefined') require('lazysizes'); // we need a conditional require since lazysizes can't run on server

const DEFAULT_IMAGE = 'data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkkAQAAB8AG7jymN8AAAAASUVORK5CYII=';

const WIDTH_STYLE = {
  sm: 'sm',
  md: 'sm',
  lg: 'lg'
};

class Image extends Component {
  static propTypes = {
    defaultImage: PropTypes.string,
    src: PropTypes.string,
    lastEditedDate: PropTypes.string,
    alt: PropTypes.string,
    lazyLoad: PropTypes.bool,
    imageClassNames: PropTypes.string,
    imageOnLoad: PropTypes.func,
    imageStyles: PropTypes.object,
    imageBreakpoints: PropTypes.array,
    enableImgOpt: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number
    ]),
    forceDPR: PropTypes.bool,
    width: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    height: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    widthStyle: PropTypes.oneOf(Object.keys(WIDTH_STYLE))
  }

  static contextTypes = {
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    src: '',
    alt: 'Image',
    lazyLoad: true,
    imageClassNames: '',
    imageStyles: {
          objectFit: 'cover'
    },
    imageBreakpoints: [
      /* Breakpoints: XS: 0-767, S: 768-991, M: 992-1199, L: 1200-1469, XL: 1470-  */
      // these define the breakpoints (maxWidth), and which rendition (widthPX or widthVW) to use for that breakpoint.
      {'name': 'xs', 'maxWidth': 768,  'widthVW': 50}, // assumes default images are 50% wide under 768px breakpoints
      {'name': 'sm', 'maxWidth': 992,  'widthVW': 33}, // assumes default images are 33% wide under 992px breakpoints
      {'name': 'md', 'maxWidth': 1200, 'widthVW': 25}, // assumes default images are 25% wide under 1200px breakpoints
      {'name': 'lg', 'widthPX': 768 } // assumes default images are 768px wide above 1200px breakpoint
    ],
    enableImgOpt: isImgOptEnabled(), // feature flag to enable via process.env var (rendered to franklyDataLayer)
    forceDPR: false,
    width: '',
    height: '',
    widthStyle: 'sm'
  }

  componentWillUpdate(nextProps) {
    if (this.props.src && nextProps.src && this.props.src !== nextProps.src
        && this.image && this.image.classList.contains('lazyloaded')) {
      this.image.classList.replace('lazyloaded', 'lazyload');
    }
  }

  render() {
    let { lazyLoad, width, imageStyles, imageClassNames} = this.props;
    const {
      defaultImage,
      src = '',
      lastEditedDate = '',
      alt,
      imageOnLoad,
      imageBreakpoints,
      enableImgOpt,
      forceDPR,
      height,
    } = this.props;

    if (imageStyles === null) { // for components who pass in a null value
      imageStyles = {
        objectFit: 'cover' // set our default
      };
    }

    const {
      config: {
        appOptions: {
          placeholderImage
        } = {}
      }
    } = this.context;

    let defaultSrc = defaultImage || placeholderImage || DEFAULT_IMAGE;

    let builtSrc = src.trim(); // strip any trailing whitespace
    let srcset = '';  // default value
    let sizes = '';
    let addImageClassNames = imageClassNames; // we are adding in object-fit polyfill classname to those that come from props

    function handleError(err) { // need to blank out both srcset and sizes to avoid infinite looping
      err.target.setAttribute("srcset", '');
      err.target.setAttribute("sizes", '');
      if (err.target.getAttribute("src") === builtSrc) { //if the builtSrc is failing, try defaultImage.
        err.target.setAttribute("src", defaultImage);
      } else if (err.target.getAttribute("src") === defaultImage) { //if the defaultImage is failing, try placeholderImage.
        err.target.setAttribute("src", placeholderImage);
      } else { // otherwise, fall back to DEFAULT_IMAGE so we don't loop!
        err.target.setAttribute("src", DEFAULT_IMAGE);
      }
    }

    if (enableImgOpt) {  // If feature is on
      let query = '?auto=webp&disable=upscale' + (forceDPR ? '&dpr=2' : ''); // add basic img opt to all img refs

      if (width && (width === 'auto' || width > 0)) {
        query += `&width=${width}` + (height && height !== 'auto' ? `&height=${height}&fit=crop` : ''); // if we got a hard coded width, only set the src, not the srcset
      } else if (height && (height === 'auto' || height > 0)) { // we didn't get a width, but we did get a height, use it only
        query += `&height=${height}&fit=bounds`;
      } else {
        if (/images\.worldnow\.com|imagesdsys1\.worldnow\.com|imagesdua1\.worldnow\.com/.test(builtSrc) || (!builtSrc && defaultSrc !== DEFAULT_IMAGE)) {
          // only createTransforms if it's a Fastly ImgOpt image. Otherwise, dynamic resizing doesn't apply. Also append 'query' before createTransforms
          let ret = createTransforms(builtSrc ? buildURLFingerprinting({url: builtSrc + query, lastEditedDate, queryStr: '&'}) : defaultSrc + query, imageBreakpoints, height); // here we create the fastly transformations for responsive height and without - default value
          srcset = ret.srcset;
          sizes = ret.sizes;
        }
      }

      if (/images\.worldnow\.com|imagesdsys1\.worldnow\.com|imagesdua1\.worldnow\.com/.test(builtSrc)){
        builtSrc = buildURLFingerprinting({url: builtSrc + query, lastEditedDate, queryStr: '&'});
      }

      if (/images\.worldnow\.com|imagesdsys1\.worldnow\.com|imagesdua1\.worldnow\.com/.test(defaultSrc) && defaultSrc !== DEFAULT_IMAGE){
        defaultSrc += query;
      }
    } else {
      lazyLoad = false; // disable lazyloading, regardless of props, if the IMAGE_OPT_ENABLED is disabled
    }

    let finalSrc = DEFAULT_IMAGE, finalDataSrc = null, finalSizes = null, finalDataSizes = null, finalSrcSet = null, finalDataSrcSet = null;

    if (lazyLoad) {
      finalDataSrc = builtSrc ? builtSrc : defaultSrc;
      finalDataSizes = sizes;
      finalDataSrcSet = srcset;
      addImageClassNames = addImageClassNames + ' lazyload';
    } else {
      finalSrc = builtSrc ? builtSrc : defaultSrc;
      finalSizes = sizes;
      finalSrcSet = srcset;
    }

    if (!builtSrc && !width) {  // if we don't have a builtsrc AND width, it means we're using an unsized default and therefore should set the default image to stretch/shrink to 100% of the space.
      width = '100%';
      imageStyles =  { objectFit: 'contain' }; // needed in case placeholder images are larger than the available slot
    }

    if (typeof navigator !== 'undefined') { // required for SSR, since navigator doesn't exist on server
      const isIE11 = !!navigator.userAgent.match(/Trident.*rv\:11\./);
      const isIE10 = navigator.userAgent.match(/MSIE 10/i);
      const isIE9 = navigator.userAgent.match(/MSIE 9/i);
      const edge = navigator.userAgent.match(/Edge/i);
      if (edge || isIE11 || isIE10 || isIE9) {
        if (imageStyles !== null && imageStyles.hasOwnProperty('objectFit')) {
          if (imageStyles['objectFit'] === 'cover') {
            addImageClassNames += ' objFit-cover';
          } else if (imageStyles['objectFit'] === 'contain') {
            addImageClassNames += ' objFit-contain';
          }
        }
      }
    }
    //WEB-4642: fix bug image does not display on IE when widthStyle is 'lg' or 'md'
    let imageContainer = 'imageContainer';
    if (typeof document !== 'undefined') {
      imageContainer = document.documentMode && this.props.widthStyle !== 'sm' ? 'Card-split-imageContainer--isIE' : 'imageContainer';
    }

    return (
      <div className={imageContainer}>
        <img
          ref={(image) => this.image = image}
          onError={handleError}
          className={addImageClassNames ? addImageClassNames : null}

          sizes={finalSizes ? finalSizes : null}
          data-sizes={finalDataSizes ? finalDataSizes : null}

          srcSet={finalSrcSet ? finalSrcSet : null}
          data-srcSet={finalDataSrcSet ? finalDataSrcSet : null}

          src={finalSrc ? finalSrc : null}
          data-src={finalDataSrc ? finalDataSrc : null}

          alt={alt ? alt : 'Image'}
          style={imageStyles ? imageStyles : null}
          width={width ? width : null}
          height={height ? height : null}
          onLoad={imageOnLoad}
        />
      </div>
    );
  }
}

function isImgOptEnabled(){
   if (typeof window !== 'undefined' && window.franklyDataLayer[0].IMAGE_OPT_ENABLED === false) {
     return false;  // Client Side Rendering
   } else if (process.env.IMAGE_OPT_ENABLED === 'false'
     || process.env.IMAGE_OPT_ENABLED === '0') {
     return false; // Server Side Rendering
   } else {
     return true;
   }
} // feature flag to enable via process.env var (rendered to franklyDataLayer)

function isImgOptRetinaEnabled(){
  if (typeof window !== 'undefined' && window.franklyDataLayer[0].IMAGE_OPT_RETINA) {
    return true;  // Client Side Rendering
  } else if (process.env.IMAGE_OPT_RETINA) {
    return true; // Server Side Rendering
  } else {
    return false;
  }
}
// loop through imageBreakpoints and generate srcset/sizes for each
function createTransforms(builtSrc, imageBreakpoints, height) {
  let srcset = '', sizes = '';
  for (let i in imageBreakpoints) {
    if (imageBreakpoints[i].widthPX){ // if we got a width in pixels, set the rendition options ("srcset")
      srcset += builtSrc + '&width=' + imageBreakpoints[i].widthPX     + (height ? `&height=${height}&fit=crop` : '') + ' ' + imageBreakpoints[i].widthPX + 'w';

      if (isImgOptRetinaEnabled()) {
        srcset += ', ' + builtSrc + '&width=' + imageBreakpoints[i].widthPX * 2 + (height ? `&height=${height * 2}&fit=crop` : '') + ' ' + imageBreakpoints[i].widthPX * 2 + 'w'; // retina
      }

      if (imageBreakpoints[i].maxWidth){  // also, if we got a maxWidth, set the sizes to be used per breakpoint ("sizes")
        sizes += '(max-width: ' + imageBreakpoints[i].maxWidth + 'px) ' + imageBreakpoints[i].widthPX + 'px';
      } else {
        sizes += imageBreakpoints[i].widthPX + 'px';
      }
    } else {
      if (imageBreakpoints[i].maxWidth && imageBreakpoints[i].widthVW){ // if we got viewport widths
        const relativeWidth = Math.round(imageBreakpoints[i].maxWidth * imageBreakpoints[i].widthVW / 100);
        srcset += `${builtSrc}&width=` + relativeWidth     + (height ? `&height=${height}&fit=crop` : '') + ' ' + relativeWidth + 'w';

        if (isImgOptRetinaEnabled()) {
          srcset += ', ' + `${builtSrc}&width=` + relativeWidth * 2 + (height ? `&height=${height * 2}&fit=crop` : '') + ' ' + relativeWidth * 2 + 'w'; // retina
        }
        sizes += '(max-width: ' + imageBreakpoints[i].maxWidth + 'px) ' + relativeWidth + 'px';
      }
    }

    if (i < imageBreakpoints.length - 1) { // append commas between breakpoints, as long as we're not in the last breakpoint
      srcset += ', ';
      sizes += ', ';
    }
  }

  return { srcset, sizes };
}

export default Image;



// WEBPACK FOOTER //
// ./app/components/Image.js